import { useState, useEffect } from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock3, ExternalLink } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Chapter, MaskLines } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { api } from "../lib/api";
import { FIRM, WHATSAPP_URL, MAPS_EMBED_URL, MAPS_LINK } from "../data/content";

export default function Contact() {
  const [hours, setHours] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setHours(r.data.business_hours)).catch(() => {});
  }, []);

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Contact CA Neeraj Battan, Neeraj Battan & Associates, Chartered Accountants — Plot No. 1, 2, 3, KDB Road, Kurukshetra, Haryana 136118. Call +91 90691 07777 or email ca.neerajbattan@gmail.com."
        path="/contact"
      />

      <section className="relative bg-navy text-white overflow-hidden" data-testid="contact-hero">
        <div className="grain-overlay" />
        <div className="absolute -top-40 right-0 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-24">
          <p className="eyebrow mb-6">Contact Us</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="contact-heading">
            <MaskLines lines={["Let's Discuss Your"]} />
            <MaskLines lines={["Requirements."]} lineClassName="text-gold italic" baseDelay={0.3} />
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <Chapter num="01" label="Reach Us" />
              <Reveal>
                <h2 className="font-serif text-3xl font-semibold text-navy">{FIRM.principal}</h2>
                <p className="mt-1 text-sm font-mono uppercase tracking-[0.2em] text-gold">{FIRM.name} · {FIRM.qualification}</p>
                <div className="mt-8 space-y-5" data-testid="contact-details">
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center shrink-0"><MapPin size={18} className="text-gold" /></span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Office Address</p>
                      <p className="text-slate-700 text-sm leading-relaxed">
                        {FIRM.addressLines[0]}<br />{FIRM.addressLines[1]}<br />{FIRM.addressLines[2]}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center shrink-0"><Phone size={18} className="text-gold" /></span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Phone</p>
                      <a href={`tel:${FIRM.phone1Raw}`} className="block text-slate-700 text-sm hover:text-gold transition-colors" data-testid="contact-phone-1">{FIRM.phone1}</a>
                      <a href={`tel:${FIRM.phone2Raw}`} className="block text-slate-700 text-sm hover:text-gold transition-colors" data-testid="contact-phone-2">{FIRM.phone2} <span className="text-slate-400">(Alternative)</span></a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center shrink-0"><Mail size={18} className="text-gold" /></span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Email</p>
                      <a href={`mailto:${FIRM.email}`} className="text-slate-700 text-sm hover:text-gold transition-colors break-all" data-testid="contact-email">{FIRM.email}</a>
                    </div>
                  </div>
                  {hours && (
                    <div className="flex items-start gap-4" data-testid="business-hours-block">
                      <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center shrink-0"><Clock3 size={18} className="text-gold" /></span>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Business Hours</p>
                        {hours.split("\n").map((line, i) => (
                          <p key={i} className="text-slate-700 text-sm">{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href={`tel:${FIRM.phone1Raw}`} data-testid="contact-call-button" className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-light transition-colors">
                    <Phone size={15} className="text-gold" /> Call Now
                  </a>
                  <a href={`mailto:${FIRM.email}`} data-testid="contact-email-button" className="inline-flex items-center gap-2 rounded-full border border-navy/30 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                    <Mail size={15} className="text-gold" /> Email Us
                  </a>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="inline-flex items-center gap-2 rounded-full border border-navy/30 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                    <MessageCircle size={15} className="text-gold" /> WhatsApp Us
                  </a>
                </div>
              </Reveal>

              <Reveal delay={0.15} className="mt-12">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md" data-testid="contact-map-block">
                  <iframe
                    title="Office location — Neeraj Battan & Associates"
                    src={MAPS_EMBED_URL}
                    className="w-full h-72 border-0"
                    loading="lazy"
                    data-testid="contact-map-embed"
                  />
                  <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-white py-3 text-xs font-semibold text-navy hover:text-gold transition-colors" data-testid="contact-open-maps">
                    Open in Google Maps <ExternalLink size={13} />
                  </a>
                </div>
              </Reveal>
            </div>

            <div>
              <Chapter num="02" label="Enquiry" />
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl font-semibold text-navy mb-8">Consultation Enquiry Form</h2>
                <EnquiryForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
